// Teams-aware authentication helpers.
//
// HelpDesk Pro runs in two hosts: a normal browser tab (existing redirect login,
// unchanged) and inside Microsoft Teams as a personal tab. Inside Teams, Entra
// refuses to render its login page in an iframe, so we:
//   1) try a SILENT sign-in first (ssoSilent with the Teams user's loginHint —
//      usually succeeds with no UI because the user is already signed into Teams), and
//   2) fall back to a Teams-controlled popup (authentication.authenticate) that
//      loads /auth-start, which runs the normal MSAL redirect inside the popup.

import { app, authentication } from '@microsoft/teams-js';
import { IPublicClientApplication } from '@azure/msal-browser';
import { loginRequest } from './msalConfig';

let cachedInTeams: boolean | null = null;

const inIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    return true; // cross-origin parent — definitely framed
  }
};

/**
 * Detect whether we are running inside a Microsoft Teams host (desktop, web,
 * or mobile). Cheap short-circuit when clearly not framed; otherwise confirm
 * by initializing the Teams SDK with a timeout so a non-Teams iframe can't
 * hang us.
 */
export async function isInTeams(): Promise<boolean> {
  if (cachedInTeams !== null) return cachedInTeams;
  if (!inIframe() && !navigator.userAgent.includes('Teams')) {
    cachedInTeams = false;
    return false;
  }
  try {
    await Promise.race([
      app.initialize(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('teams-js init timeout')), 2000)
      ),
    ]);
    cachedInTeams = true;
  } catch {
    cachedInTeams = false;
  }
  return cachedInTeams;
}

/** The signed-in Teams user's UPN/email, used as a loginHint. */
export async function getTeamsLoginHint(): Promise<string | undefined> {
  try {
    const ctx = await app.getContext();
    return ctx.user?.loginHint || ctx.user?.userPrincipalName || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Attempt a fully silent sign-in. Returns true on success.
 * Checks the MSAL cache first (covers the post-popup case in the web client),
 * then falls back to ssoSilent against the Entra session cookie.
 */
export async function teamsSilentLogin(
  instance: IPublicClientApplication
): Promise<boolean> {
  const hint = await getTeamsLoginHint();

  // Already cached from a previous session or from the auth popup?
  const cached =
    (hint &&
      instance
        .getAllAccounts()
        .find((a) => a.username.toLowerCase() === hint.toLowerCase())) ||
    instance.getAllAccounts()[0];
  if (cached) {
    instance.setActiveAccount(cached);
    try {
      // Prove the cached account can actually mint a token; throws if not.
      await instance.acquireTokenSilent({ ...loginRequest, account: cached });
      return true;
    } catch {
      /* fall through to ssoSilent */
    }
  }

  if (!hint) return false;
  try {
    const result = await instance.ssoSilent({ ...loginRequest, loginHint: hint });
    instance.setActiveAccount(result.account);
    return true;
  } catch {
    return false;
  }
}

/**
 * Interactive fallback: Teams opens a small popup it controls, pointed at our
 * /auth-start page. That page runs loginRedirect; Entra bounces back to
 * /auth-end, which reports completion to Teams. We then finish silently.
 */
export async function teamsInteractiveLogin(
  instance: IPublicClientApplication
): Promise<void> {
  const hint = await getTeamsLoginHint();
  const url =
    `${window.location.origin}/auth-start` +
    (hint ? `?loginHint=${encodeURIComponent(hint)}` : '');

  await authentication.authenticate({ url, width: 600, height: 535 });

  const ok = await teamsSilentLogin(instance);
  if (!ok) {
    throw new Error(
      'Sign-in window completed, but a token could not be acquired silently.'
    );
  }
}

/**
 * Native Teams SSO token (the iOS-safe path).
 *
 * authentication.getAuthToken() asks the Teams host for an Entra access token
 * for THIS app's API. Teams resolves the target resource from the manifest's
 * webApplicationInfo (id = frontend client, resource = api://...-backend...),
 * and returns a token whose audience is that api:// URI — which the backend
 * accepts (JWT_AUDIENCE_TEAMS). No popup, no redirect: works inside the iOS
 * Teams webview where acquireTokenPopup is blocked.
 *
 * Returns the access token string, or throws if the host can't provide one.
 */
export async function getTeamsToken(): Promise<string> {
  // Ensure the Teams SDK is initialized (isInTeams() does this, but be defensive
  // in case this is called directly).
  try {
    await app.initialize();
  } catch {
    /* already initialized, or will throw below if truly unavailable */
  }
  return authentication.getAuthToken();
}

